import { use } from 'react';
import { useTranslation } from 'react-i18next';
import Battery1BarRounded from '@mui/icons-material/Battery1BarRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CloudOffOutlined from '@mui/icons-material/CloudOffOutlined';
import CloudOutlined from '@mui/icons-material/CloudOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';

import MonitoringBar from 'features/tenants/components/monitoring-bar/monitoring-bar';
import TenantMonitoringItem from 'features/tenants/components/tenant-monitoring-item/tenant-monitoring-item';
import { AggregatedType } from 'features/tenants/models/aggregated-monitor-data';
import { Monitoring, TenantMonitoringType } from 'features/tenants/models/tenant';
import TenantsProviderContext from 'features/tenants/providers/tenants-provider/contexts/tenants-provider-context';
import styles from './tenant-monitoring-details.module.scss';

const TenantMonitoringDetails = (): React.JSX.Element => {
  const { t } = useTranslation('tenants');
  const { currentTenant } = use(TenantsProviderContext);

  const findMonitoring = (monitorings: Monitoring[], type: TenantMonitoringType): Monitoring | undefined =>
    monitorings.find((m: Monitoring) => m.type === type);

  const renderMonitoringItem = (label: string, count: number, color: string, icon: React.JSX.Element) => (
    <TenantMonitoringItem
      key={label}
      icon={icon}
      label={label}
      count={
        <Typography variant="h2" sx={{ color: count > 0 ? color : Constants.Colors.onSurface }}>
          {count}
        </Typography>
      }
    />
  );

  const renderConnectorMonitoring = () => {
    const connectorMonitoring = currentTenant?.connectorMonitorings ?? [];
    const offlineGateway = findMonitoring(connectorMonitoring, TenantMonitoringType.Offline);
    const onlineGateway = findMonitoring(connectorMonitoring, TenantMonitoringType.Online);

    return (
      <div className={styles.tenantMonitoringContainer}>
        <Typography variant="h5">{t('tenantDetails.connectors.title')}</Typography>
        <MonitoringBar monitoring={connectorMonitoring} aggregatedType={AggregatedType.Connectors} />
        <div className={styles.tenantMonitoringItems}>
          {offlineGateway &&
            renderMonitoringItem(
              t('tenantDetails.connectors.offline'),
              offlineGateway.count,
              Constants.Colors.error,
              <CloudOffOutlined sx={{ color: Constants.Colors.error }} />,
            )}
          {onlineGateway &&
            renderMonitoringItem(
              t('tenantDetails.connectors.online'),
              onlineGateway.count,
              Constants.Colors.success,
              <CloudOutlined sx={{ color: Constants.Colors.success }} />,
            )}
        </div>
      </div>
    );
  };

  const renderGatewayMonitoring = () => {
    const gatewayMonitoring = currentTenant?.gatewayMonitorings ?? [];
    const offlineGateway = findMonitoring(gatewayMonitoring, TenantMonitoringType.Offline);
    const passiveOfflineGateway = findMonitoring(gatewayMonitoring, TenantMonitoringType.PassiveGatewayOffline);
    const onlineGateway = findMonitoring(gatewayMonitoring, TenantMonitoringType.Online);

    return (
      <div className={styles.tenantMonitoringContainer}>
        <Typography variant="h5">{t('tenantDetails.gateways.title')}</Typography>
        <MonitoringBar monitoring={gatewayMonitoring} aggregatedType={AggregatedType.Gateways} />
        <div className={styles.tenantMonitoringItems}>
          {offlineGateway &&
            renderMonitoringItem(
              t('tenantDetails.gateways.offline'),
              offlineGateway.count,
              Constants.Colors.error,
              <CloudOffOutlined sx={{ color: Constants.Colors.error }} />,
            )}
          {passiveOfflineGateway &&
            renderMonitoringItem(
              t('tenantDetails.gateways.warning'),
              passiveOfflineGateway.count,
              Constants.Colors.brightOrange,
              <WarningAmberRounded sx={{ color: Constants.Colors.brightOrange }} />,
            )}
          {onlineGateway &&
            renderMonitoringItem(
              t('tenantDetails.gateways.online'),
              onlineGateway.count,
              Constants.Colors.success,
              <CloudOutlined sx={{ color: Constants.Colors.success }} />,
            )}
        </div>
      </div>
    );
  };

  const renderSystemMonitoring = () => {
    const systemMonitoring = currentTenant?.systemMonitorings ?? [];
    const offlineSystem = findMonitoring(systemMonitoring, TenantMonitoringType.Offline);
    const warningSystem = findMonitoring(systemMonitoring, TenantMonitoringType.Warning);
    const onlineSystem = findMonitoring(systemMonitoring, TenantMonitoringType.Online);

    return (
      <div className={styles.tenantMonitoringContainer}>
        <Typography variant="h5">{t('tenantDetails.systems.title')}</Typography>
        <MonitoringBar monitoring={systemMonitoring} aggregatedType={AggregatedType.Systems} />
        <div className={styles.tenantMonitoringItems}>
          {offlineSystem &&
            renderMonitoringItem(
              t('tenantDetails.systems.offline'),
              offlineSystem.count,
              Constants.Colors.error,
              <CloudOffOutlined sx={{ color: Constants.Colors.error }} />,
            )}
          {warningSystem &&
            renderMonitoringItem(
              t('tenantDetails.systems.warning'),
              warningSystem.count,
              Constants.Colors.brightOrange,
              <WarningAmberRounded sx={{ color: Constants.Colors.brightOrange }} />,
            )}
          {onlineSystem &&
            renderMonitoringItem(
              t('tenantDetails.systems.online'),
              onlineSystem.count,
              Constants.Colors.success,
              <CloudOutlined sx={{ color: Constants.Colors.success }} />,
            )}
        </div>
      </div>
    );
  };

  const renderDeviceMonitoring = () => {
    const devicesMonitoring = currentTenant?.devicesMonitorings ?? [];
    const devicesOffline = findMonitoring(devicesMonitoring, TenantMonitoringType.Offline);
    const devicesOnline = findMonitoring(devicesMonitoring, TenantMonitoringType.Online);
    const devicesNotMonitored = findMonitoring(devicesMonitoring, TenantMonitoringType.NotMonitored);
    const devicesBatteryCritical = findMonitoring(devicesMonitoring, TenantMonitoringType.BatteryCritical);
    const devicesBatteryLow = findMonitoring(devicesMonitoring, TenantMonitoringType.BatteryLow);
    const devicesMalfunction = findMonitoring(devicesMonitoring, TenantMonitoringType.DeviceMalfunction);
    const batteryCombinedCount = (devicesBatteryCritical?.count ?? 0) + (devicesBatteryLow?.count ?? 0);

    return (
      <div className={styles.tenantMonitoringContainer}>
        <Typography variant="h5">{t('tenantDetails.devices.title')}</Typography>
        <MonitoringBar monitoring={devicesMonitoring} aggregatedType={AggregatedType.Devices} />
        <div className={styles.tenantMonitoringItems}>
          {devicesOffline &&
            renderMonitoringItem(
              t('tenantDetails.devices.offline'),
              devicesOffline.count,
              Constants.Colors.error,
              <CloudOffOutlined sx={{ color: Constants.Colors.error }} />,
            )}
          {devicesMalfunction &&
            renderMonitoringItem(
              t('tenantDetails.devices.warning'),
              devicesMalfunction.count,
              Constants.Colors.brightOrange,
              <WarningAmberRounded sx={{ color: Constants.Colors.brightOrange }} />,
            )}
          {(devicesBatteryCritical || devicesBatteryLow) &&
            renderMonitoringItem(
              t('tenantDetails.devices.batteryCombined'),
              batteryCombinedCount,
              Constants.Colors.brightOrange,
              <Battery1BarRounded sx={{ color: Constants.Colors.brightOrange }} />,
            )}
        </div>
        <div className={styles.tenantMonitoringItems}>
          {devicesOnline &&
            renderMonitoringItem(
              t('tenantDetails.devices.online'),
              devicesOnline.count,
              Constants.Colors.success,
              <CloudOutlined sx={{ color: Constants.Colors.success }} />,
            )}
          {devicesNotMonitored &&
            renderMonitoringItem(
              t('tenantDetails.devices.notMonitored'),
              devicesNotMonitored.count,
              Constants.Colors.onSurfaceVariant,
              <CloseRounded sx={{ color: Constants.Colors.onSurfaceVariant }} />,
            )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {renderConnectorMonitoring()}
      {renderGatewayMonitoring()}
      {renderSystemMonitoring()}
      {renderDeviceMonitoring()}
    </div>
  );
};

export default TenantMonitoringDetails;
